/**
 * Per-platform Zod output schemas for the Content Writer adapters.
 *
 * Owned by `prompts/02-features/content-writer/03a-platform-adapters-sonnet-4.6.md`.
 *
 * The legacy `contentDraftOutputSchema` (single shape for all platforms) in
 * `validators/content-writer.ts` remains the schema the v1 generation
 * service hands to the AI provider. These per-platform schemas express
 * stricter, platform-specific shapes (Twitter/Threads thread arrays,
 * Instagram hashtag floor, YouTube tags + timestamps) for callers that
 * want to validate adapted output against the platform's actual contract.
 *
 * Numeric ranges mirror `PLATFORM_CONSTRAINTS` in `platform-constraints.ts`.
 * If you change a number here, update both files in the same commit.
 */

import { z } from "zod";

const hashtagSchema = z.string().min(1);

export const linkedInOutputSchema = z.object({
  content: z.string().min(1).max(3000),
  hashtags: z.array(hashtagSchema).max(5),
  characterCount: z.number().int().min(0),
});
export type LinkedInOutput = z.infer<typeof linkedInOutputSchema>;

export const twitterTweetSchema = z.object({
  content: z.string().min(1).max(280),
  index: z.number().int().min(1),
});

export const twitterOutputSchema = z.object({
  tweets: z.array(twitterTweetSchema).min(1).max(25),
  isThread: z.boolean(),
  hashtags: z.array(hashtagSchema).max(3),
});
export type TwitterOutput = z.infer<typeof twitterOutputSchema>;

export const instagramOutputSchema = z.object({
  caption: z.string().min(1).max(2200),
  hashtags: z.array(hashtagSchema).min(8).max(30),
  callToAction: z.string().min(1),
  characterCount: z.number().int().min(0),
});
export type InstagramOutput = z.infer<typeof instagramOutputSchema>;

export const youtubeOutputTimestampSchema = z.object({
  /** mm:ss or hh:mm:ss; description timestamps need at least mm:ss. */
  time: z.string().regex(/^\d+:\d{2}(?::\d{2})?$/),
  label: z.string().min(1),
});

export const youtubeOutputSchema = z.object({
  description: z.string().min(1).max(5000),
  /** Free-form tags (comma-separated in the YouTube UI). */
  tags: z.array(z.string().min(1)).max(30),
  hashtags: z.array(hashtagSchema).max(15),
  timestamps: z.array(youtubeOutputTimestampSchema).optional(),
  characterCount: z.number().int().min(0),
});
export type YouTubeOutput = z.infer<typeof youtubeOutputSchema>;

export const threadsPostSchema = z.object({
  content: z.string().min(1).max(500),
  index: z.number().int().min(1),
});

export const threadsOutputSchema = z.object({
  posts: z.array(threadsPostSchema).min(1).max(25),
  isThread: z.boolean(),
  hashtags: z.array(hashtagSchema).max(5),
});
export type ThreadsOutput = z.infer<typeof threadsOutputSchema>;

export const tiktokOutputSchema = z.object({
  caption: z.string().min(1).max(2200),
  hashtags: z.array(hashtagSchema).max(5),
  characterCount: z.number().int().min(0),
});
export type TikTokOutput = z.infer<typeof tiktokOutputSchema>;

/**
 * Discriminated union of every platform output shape, keyed by `platform`.
 * Useful for adapter callers that want a single typed envelope across all
 * 6 platforms (used by 03b orchestration).
 */
export const platformOutputByPlatform = {
  linkedin: linkedInOutputSchema,
  twitter: twitterOutputSchema,
  instagram: instagramOutputSchema,
  youtube: youtubeOutputSchema,
  threads: threadsOutputSchema,
  tiktok: tiktokOutputSchema,
} as const;

export type PlatformOutputByPlatform = {
  linkedin: LinkedInOutput;
  twitter: TwitterOutput;
  instagram: InstagramOutput;
  youtube: YouTubeOutput;
  threads: ThreadsOutput;
  tiktok: TikTokOutput;
};
